/**
 * Maintenance create/edit form helpers (pure — unit-tested).
 * Keeps Bexio contact id (API) distinct from contact nr (display).
 */

export const EMPTY_MAINTENANCE_FORM = Object.freeze({
  domain_name: '',
  customer_name: '',
  bexio_contact_id: null,
  bexio_contact_nr: null,
  registration_date: '',
  billing_frequency: 'monthly',
  maintenance_fee_amount: 0,
  currency: 'CHF',
  invoice_status: 'not_invoiced',
  bexio_invoice_id: '',
  hosting_provider: '',
  internal_owner: '',
  responsible_email: '',
  drip_language: 'en',
  notes: '',
  is_active: true,
  last_invoice_date: '',
  last_paid_date: '',
});

export function blankMaintenanceForm() {
  return {
    ...EMPTY_MAINTENANCE_FORM,
    bexio_contact_id: null,
    bexio_contact_nr: null,
  };
}

export function formFromMaintenanceItem(item) {
  return {
    ...blankMaintenanceForm(),
    domain_name: String(item?.domain_name || ''),
    customer_name: String(item?.customer_name || ''),
    bexio_contact_id: item?.bexio_contact_id == null || item?.bexio_contact_id === ''
      ? null
      : Number(item.bexio_contact_id),
    bexio_contact_nr: item?.bexio_contact_nr == null || item?.bexio_contact_nr === ''
      ? null
      : Number(item.bexio_contact_nr),
    registration_date: String(item?.registration_date || '').slice(0, 10),
    billing_frequency: item?.billing_frequency || 'monthly',
    maintenance_fee_amount: Number(item?.maintenance_fee_amount || 0),
    currency: item?.currency || 'CHF',
    invoice_status: item?.invoice_status || 'not_invoiced',
    bexio_invoice_id: String(item?.bexio_invoice_id || ''),
    hosting_provider: String(item?.hosting_provider || ''),
    internal_owner: String(item?.internal_owner || ''),
    responsible_email: String(item?.responsible_email || ''),
    drip_language: item?.drip_language === 'fr' ? 'fr' : 'en',
    notes: String(item?.notes || ''),
    is_active: item?.is_active !== false,
    last_invoice_date: String(item?.last_invoice_date || '').slice(0, 10),
    last_paid_date: String(item?.last_paid_date || '').slice(0, 10),
  };
}

/** Apply a Bexio contact picker selection onto the form. Persists API id, not nr. */
export function applyBexioContactToForm(form, contact) {
  const idRaw = contact?.id;
  const id = idRaw == null || idRaw === '' ? null : Number(idRaw);
  const nrRaw = contact?.nr;
  const nr = nrRaw == null || nrRaw === '' ? null : Number(nrRaw);
  const label = String(contact?.label || '').trim();
  return {
    ...form,
    customer_name: label || String(form?.customer_name || ''),
    bexio_contact_id: Number.isFinite(id) ? id : null,
    bexio_contact_nr: Number.isFinite(nr) ? nr : null,
  };
}

export function formatLinkedBexioContact(formOrItem) {
  const id = formOrItem?.bexio_contact_id;
  if (id == null || id === '') return null;
  const nr = formOrItem?.bexio_contact_nr;
  if (nr != null && nr !== '' && Number(nr) !== Number(id)) {
    return `Linked to Bexio contact id ${id} (nr #${nr})`;
  }
  return `Linked to Bexio contact id ${id}`;
}

/**
 * Validate + serialize the maintenance drawer form for create/update API.
 * Returns { ok:true, payload } or { ok:false, missing:string[], message }.
 */
export function buildMaintenanceItemPayload(form) {
  const domain_name = String(form?.domain_name ?? '').trim();
  const customer_name = String(form?.customer_name ?? '').trim();
  const registration_date = String(form?.registration_date ?? '').trim().slice(0, 10);
  const missing = [];
  if (!domain_name) missing.push('domain');
  if (!customer_name) missing.push('customer');
  if (!registration_date) missing.push('registration date');
  if (missing.length) {
    return {
      ok: false,
      missing,
      message: `Domain, customer, and registration date are required. Missing: ${missing.join(', ')}.`,
      payload: null,
    };
  }

  const contactIdRaw = form?.bexio_contact_id;
  let bexio_contact_id = null;
  if (contactIdRaw != null && contactIdRaw !== '') {
    const n = Number(contactIdRaw);
    if (!Number.isFinite(n)) {
      return {
        ok: false,
        missing: ['bexio contact'],
        message: 'Bexio contact id is invalid. Re-select the contact from the picker.',
        payload: null,
      };
    }
    bexio_contact_id = n;
  }

  const last_invoice_date = String(form?.last_invoice_date ?? '').trim().slice(0, 10) || null;
  const last_paid_date = String(form?.last_paid_date ?? '').trim().slice(0, 10) || null;

  return {
    ok: true,
    missing: [],
    message: '',
    payload: {
      domain_name,
      customer_name,
      bexio_contact_id,
      registration_date,
      billing_frequency: form?.billing_frequency || 'monthly',
      maintenance_fee_amount: Number(form?.maintenance_fee_amount || 0),
      currency: form?.currency || 'CHF',
      invoice_status: form?.invoice_status || 'not_invoiced',
      bexio_invoice_id: String(form?.bexio_invoice_id ?? '').trim() || null,
      notes: String(form?.notes ?? '').trim(),
      hosting_provider: String(form?.hosting_provider ?? '').trim() || null,
      internal_owner: String(form?.internal_owner ?? '').trim() || null,
      responsible_email: String(form?.responsible_email ?? '').trim() || null,
      drip_language: form?.drip_language === 'fr' ? 'fr' : 'en',
      is_active: form?.is_active !== false,
      last_invoice_date,
      last_paid_date,
    },
  };
}
